import { Injectable } from '@angular/core';
import { SubSalesOrder } from './misc-classes';
import { DS_TAB_CONFIG, DsFieldDef, isGroupDef } from './ds-export-config';

export interface RevisionFieldDelta {
    section: string;
    sectionKey: string;
    field: string;
    fieldLabel: string;
    fieldPath: string;
    oldValue: string;
    newValue: string;
    revision: string;
    modifiedBy: string;
    modifiedDate: string;
}

export interface RevisionVersionMeta {
    version: string;
    path: string;
    deltaPath?: string;
    createdByEmail: string;
    createdByName: string;
    createdDate: Date | string;
}

@Injectable()
export class RevisionHandlerService {

    constructor() {}

    //Returns true when all three C40, C50, C60 actual dates are filled.
    isAlphabeticRevision(subSalesOrder: SubSalesOrder): boolean {
        const engg = subSalesOrder.enggProcureIP;
        if (!engg) return false;
        return engg.actualC40Date != null && engg.actualC50Date != null && engg.actualC60Date != null;
    }

    //Compares current datasheet JSON against saved snapshot to detect actual data changes.
    hasDatasheetChanged(currentDsJson: any, savedDsJson: any): boolean {
        if (!savedDsJson) return true;
        const currentCopy = JSON.parse(JSON.stringify(currentDsJson));
        const savedCopy = JSON.parse(JSON.stringify(savedDsJson));
        delete currentCopy.versions;
        delete currentCopy.currentVersion;
        delete savedCopy.versions;
        delete savedCopy.currentVersion;
        return JSON.stringify(currentCopy) !== JSON.stringify(savedCopy);
    }

    buildRevisionChanges(previousDsJson: any, currentDsJson: any, revision: string, modifiedBy: string, modifiedDate: Date | string): RevisionFieldDelta[] {
        if (!previousDsJson || !currentDsJson) return [];

        const changes: RevisionFieldDelta[] = [];
        const modifiedDateStr = this.toIsoString(modifiedDate);

        for (const tab of DS_TAB_CONFIG) {
            const previousSection = previousDsJson[tab.sectionKey] || {};
            const currentSection = currentDsJson[tab.sectionKey] || {};

            for (const item of tab.items) {
                if (isGroupDef(item)) {
                    if (item.isRepeating && item.arrayKey && item.arrayFields) {
                        const prevArr = Array.isArray(previousSection[item.arrayKey]) ? previousSection[item.arrayKey] : [];
                        const currArr = Array.isArray(currentSection[item.arrayKey]) ? currentSection[item.arrayKey] : [];
                        const maxLen = Math.max(prevArr.length, currArr.length);

                        for (let idx = 0; idx < maxLen; idx++) {
                            const prevRow = prevArr[idx] || {};
                            const currRow = currArr[idx] || {};
                            for (const arrField of item.arrayFields) {
                                this.pushDeltaIfChanged(
                                    changes,
                                    tab.tabName,
                                    tab.sectionKey,
                                    arrField,
                                    `${tab.sectionKey}.${item.arrayKey}[${idx}].${arrField.key}`,
                                    prevRow[arrField.key],
                                    currRow[arrField.key],
                                    revision,
                                    modifiedBy,
                                    modifiedDateStr
                                );
                            }
                        }
                        continue;
                    }

                    for (const field of item.fields) {
                        this.pushDeltaIfChanged(
                            changes,
                            tab.tabName,
                            tab.sectionKey,
                            field,
                            `${tab.sectionKey}.${field.key}`,
                            previousSection[field.key],
                            currentSection[field.key],
                            revision,
                            modifiedBy,
                            modifiedDateStr
                        );
                    }
                    continue;
                }

                this.pushDeltaIfChanged(
                    changes,
                    tab.tabName,
                    tab.sectionKey,
                    item,
                    `${tab.sectionKey}.${item.key}`,
                    previousSection[item.key],
                    currentSection[item.key],
                    revision,
                    modifiedBy,
                    modifiedDateStr
                );
            }

            // Track section-level notes so free-text notes changes surface as deltas.
            const noteDefs: { key: string; label: string }[] = [
                { key: 'sectionNotePM', label: 'PM Note' },
                { key: 'sectionNoteEE', label: 'EE Note' },
                { key: 'sectionNoteME', label: 'ME Note' }
            ];
            for (const nd of noteDefs) {
                this.pushDeltaIfChanged(
                    changes,
                    tab.tabName,
                    tab.sectionKey,
                    { key: nd.key, label: nd.label } as DsFieldDef,
                    `${tab.sectionKey}.${nd.key}`,
                    previousSection[nd.key],
                    currentSection[nd.key],
                    revision,
                    modifiedBy,
                    modifiedDateStr
                );
            }
        }

        return changes;
    }



    isRevisionIntelligenceEnabled(ds: any): boolean {
        if (!ds) return false;
        const versionsLen = Array.isArray(ds.versions) ? ds.versions.length : 0;
        const currentVersion = ds.currentVersion || '';
        if (versionsLen === 0) return false;
        return currentVersion !== 'Rev-0';
    }

    //Numeric: Rev-0 → Rev-1 → Rev-2 ...
    //Alphabetic: Rev-A → Rev-B → Rev-C , here triggered when C40+C50+C60 all filled
    getNextRevisionNumber(currentVersion: string, useAlphabetic: boolean): string {
        if (!currentVersion) {
            return useAlphabetic ? 'Rev-A' : 'Rev-0';
        }

        const parts = currentVersion.split('-');
        const suffix = parts.length > 1 ? parts[1] : '0';

        if (useAlphabetic) {
            if (!isNaN(Number(suffix))) {
                return 'Rev-A';
            }
            return 'Rev-' + this.incrementAlpha(suffix);
        } else {
            const num = isNaN(Number(suffix)) ? 0 : Number(suffix);
            return 'Rev-' + (num + 1);
        }
    }

    /**
     * Generates the BLOB storage path for a version snapshot.
     * Format: datasheet/{soNo}/{soNo}_{ssoNum}_{Rev}.json
     * Strips legacy "Sub_Order_" / "SO_" prefixes from the inputs so the
     * file name is e.g. 4201849991_02_Rev-1.json (not Sub_Order_02 / SO_42018...).
     */
    generateVersionPath(salesOrderNo: string, subOrderNoId: string, revisionNo: string): string {
        const so = this.normalizeSaleOrderNo(salesOrderNo);
        const sub = this.normalizeSubOrderId(subOrderNoId);
        return `datasheet/${so}/${so}_${sub}_${revisionNo}.json`;
    }

    /**
     * Generates the BLOB storage path for a revision's field-level delta JSON.
     * Format: datasheet-revision-summary/{soNo}/{soNo}_{ssoNum}_{Rev}_Delta.json
     * Kept separate from the Sales Order DB document so revision growth does not
     * bloat the main document.
     */
    generateDeltaPath(salesOrderNo: string, subOrderNoId: string, revisionNo: string): string {
        const so = this.normalizeSaleOrderNo(salesOrderNo);
        const sub = this.normalizeSubOrderId(subOrderNoId);
        return `datasheet-revision-summary/${so}/${so}_${sub}_${revisionNo}_Delta.json`;
    }

    private normalizeSubOrderId(id: string): string {
        if (!id) return '';
        return String(id).replace(/^(sub_order_|sub-)/i, '');
    }

    private normalizeSaleOrderNo(no: string): string {
        if (!no) return '';
        return String(no).replace(/^so_/i, '');
    }

    //Creates a version entry object for the versions array.
    createVersionEntry(version: string, path: string, deltaPath: string, createdByEmail: string, createdByName: string, createdDate: Date): RevisionVersionMeta {
        return {
            version: version,
            path: path,
            deltaPath: deltaPath,
            createdByEmail: createdByEmail,
            createdByName: createdByName,
            createdDate: createdDate
        };
    }

    private pushDeltaIfChanged(
        changes: RevisionFieldDelta[],
        section: string,
        sectionKey: string,
        fieldDef: DsFieldDef,
        fieldPath: string,
        oldValueRaw: any,
        newValueRaw: any,
        revision: string,
        modifiedBy: string,
        modifiedDate: string
    ): void {
        const oldComparable = this.toComparableValue(oldValueRaw);
        const newComparable = this.toComparableValue(newValueRaw);
        if (oldComparable === newComparable) return;

        changes.push({
            section,
            sectionKey,
            field: fieldDef.key,
            fieldLabel: fieldDef.label,
            fieldPath,
            oldValue: this.stringifyValue(oldValueRaw),
            newValue: this.stringifyValue(newValueRaw),
            revision,
            modifiedBy: modifiedBy || '',
            modifiedDate
        });
    }

    private toComparableValue(value: any): string {
        if (value === undefined || value === null) return '';
        if (typeof value === 'string') return value.trim();
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        return JSON.stringify(this.sortObject(value));
    }

    private stringifyValue(value: any): string {
        if (value === undefined || value === null) return '';
        if (typeof value === 'string') return value.trim();
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        try {
            return JSON.stringify(value);
        } catch (_e) {
            return String(value);
        }
    }

    private sortObject(value: any): any {
        if (Array.isArray(value)) {
            return value.map(v => this.sortObject(v));
        }
        if (value && typeof value === 'object') {
            const sorted: any = {};
            Object.keys(value).sort().forEach(k => {
                sorted[k] = this.sortObject(value[k]);
            });
            return sorted;
        }
        return value;
    }

    private toIsoString(date: Date | string): string {
        if (!date) return new Date().toISOString();
        if (date instanceof Date) return date.toISOString();
        const parsed = new Date(date);
        if (isNaN(parsed.getTime())) return new Date().toISOString();
        return parsed.toISOString();
    }

    private incrementAlpha(alpha: string): string {
        if (alpha.length === 0) return 'A';
        const lastChar = alpha.charCodeAt(alpha.length - 1);
        if (lastChar < 90) {
            return alpha.substring(0, alpha.length - 1) + String.fromCharCode(lastChar + 1);
        }
        if (alpha.length === 1) return 'AA';
        return this.incrementAlpha(alpha.substring(0, alpha.length - 1)) + 'A';
    }
}
